/**
 * Direction-control shim for `document.startViewTransition`.
 *
 * Why we need this: `next-view-transitions` calls the API generically
 * without knowing whether a navigation is a forward push or a history
 * back. We patch the global so the right `<html data-nav-direction>`
 * value is written immediately BEFORE the browser captures the
 * outgoing snapshot, then we delegate to the original implementation.
 *
 * Browser-support note: iOS Safari < 17.7 and older Android WebViews
 * do not expose `document.startViewTransition` at all. This shim is a
 * no-op in that case (the install function returns an empty teardown
 * and never patches the global). Callers should additionally gate
 * mounting via `useViewTransitionsEnabled()` to avoid running the
 * surrounding effect needlessly.
 */

export type NavDirection = "forward" | "back";

interface InstallArgs {
  html: HTMLElement;
  /**
   * Returns the current popstate-active flag and clears it. The
   * caller (OwletNavDirectionTracker) owns the popstate listener and
   * the flag itself; this shim only consumes.
   *
   * When true, the popstate listener has already written
   * `data-nav-direction="back"` synchronously — we leave it alone and
   * just consume the flag so the next nav doesn't carry it forward.
   */
  consumePopstate: () => boolean;
  /**
   * Returns the explicit one-shot direction override (set by
   * `setOwletNextDirection`) and clears it. Used for step-machine
   * pops (paywall dismiss etc.) where there is no popstate event.
   */
  consumeOverride: () => NavDirection | undefined;
}

export function installStartViewTransitionShim({
  html,
  consumePopstate,
  consumeOverride,
}: InstallArgs): () => void {
  const originalSVT = document.startViewTransition;
  if (typeof originalSVT !== "function") return () => {};

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const patched: typeof document.startViewTransition = function (cb: any) {
    if (consumePopstate()) {
      // popstate listener already wrote "back". Nothing more to do.
    } else {
      const override = consumeOverride();
      html.dataset.navDirection = override ?? "forward";
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (originalSVT as any).call(document, cb);
  } as typeof document.startViewTransition;

  document.startViewTransition = patched;

  return () => {
    if (document.startViewTransition === patched) {
      document.startViewTransition = originalSVT;
    }
  };
}
